import { ehrErrorDefinition, isEhrErrorCode, type EhrErrorCode } from '@vagusx/ehr-types';
import { HttpError } from './errors';

/**
 * The standard staff message for a stored EHR error (ADR-253): the registry's
 * text for a registered code, the same for every EHR and keyed by code so it
 * can be translated. Without a registered code, the stored text is shown as is.
 */
export function ehrErrorMessage(
  code: string | null | undefined,
  fallback: string | null | undefined
): string | null {
  if (isEhrErrorCode(code)) return ehrErrorDefinition(code).message;
  return fallback ?? null;
}

/** The EHR failure behind an API error, as the voice agent needs it (ADR-254). */
export interface EhrFailure {
  code: EhrErrorCode;
  /** The registry's description, written for the voice agent's model. */
  description: string;
  /** A person must act first (a credential, the set-up): no retry or other time will succeed. */
  needsHumanAction: boolean;
}

/**
 * The registered EHR failure an API error carries in `details.ehrErrorCode`
 * (e.g. a `BOOKING_FAILED` the EHR refused, ADR-252), or null when the error
 * isn't an EHR failure (a local conflict, a validation error, a network error).
 */
export function ehrFailureOf(err: unknown): EhrFailure | null {
  if (!(err instanceof HttpError)) return null;
  const code = err.details?.ehrErrorCode;
  if (typeof code !== 'string' || !isEhrErrorCode(code)) return null;
  const def = ehrErrorDefinition(code);
  return { code, description: def.description, needsHumanAction: def.needsHumanAction };
}
